import React from "react"
import { useForm } from "react-hook-form"

import * as Geo from "@/base/geo"
import { trackEvent } from "@/base/segment"
import { useConvertSensorValue } from "@/base/sensor-conversions"
import { isNumberValid } from "@/base/type-guards"
import { usePermissionCheckedAction } from "@/hooks/usePermissionCheckedAction"
import * as Models from "@/redux/models"
import { getSwathGeometry } from "@/redux/reel-runs.reducer"
import {
  getActiveFarmCoordinatesFromState,
  getDeviceSummaryByDeviceIdFromState,
} from "@/redux/selectors"
import { useShallowEqualSelector } from "@/redux/useRootSelector"

import type { AcceptsChildren } from "@/theme"

interface ProviderProps {
  /**
   * Fallback for when there is no reel run record in state (e.g. the reel is
   * retracting but the run was filtered out of LoadDeviceActivity). Forms that
   * only need the device's last reel event still work in that case.
   */
  deviceId?: string
  selectedRunId: number | undefined
}

function useSelectedReelRun({
  deviceId: fallbackDeviceId,
  selectedRunId,
  ...rest
}: ProviderProps) {
  const withPermissions = usePermissionCheckedAction()
  const farmLocation = useShallowEqualSelector(
    getActiveFarmCoordinatesFromState,
  )
  const [actionName, setActionName] = React.useState<
    "setApplicationType" | "setField" | "setReelDistance" | "setRunDirection"
  >()
  const runData = useShallowEqualSelector((state) =>
    isNumberValid(selectedRunId)
      ? Models.reelRunsActive.selectById(state, selectedRunId)
      : undefined,
  )

  const deviceId = runData?.deviceId ?? fallbackDeviceId ?? ""
  const deviceSummary = useShallowEqualSelector((state) =>
    getDeviceSummaryByDeviceIdFromState(state, deviceId),
  )
  const reelEvent = useShallowEqualSelector(
    (state) => Models.deviceEventLast.selectById(state, deviceId)?.reel,
  )
  const convertDistance = useConvertSensorValue("runDistanceMmCurrent")

  const defaultDistanceMmCurrent = reelEvent?.runDistanceMmCurrent ?? 0
  const defaultDistanceMmMax = reelEvent?.runDistanceMmMax ?? 0
  const defaultDirection =
    runData?.directionOverrideAzimuthDegrees ??
    runData?.extendHeadingDegrees ??
    0
  const form = useForm({
    defaultValues: {
      directionOverride: defaultDirection,
      distanceCurrent: convertDistance(defaultDistanceMmCurrent, "user"),
      distanceMax: convertDistance(defaultDistanceMmMax, "user"),
    },
  })
  /**
   * Because the form never unmounts, we need to manually update it here,
   * 'subscription' style
   */
  React.useEffect(() => {
    if (isNumberValid(defaultDistanceMmCurrent)) {
      form.setValue(
        "distanceCurrent",
        convertDistance(defaultDistanceMmCurrent, "user"),
      )
    }
    if (isNumberValid(defaultDistanceMmMax)) {
      form.setValue(
        "distanceMax",
        convertDistance(defaultDistanceMmMax, "user"),
      )
    }

    if (isNumberValid(defaultDirection)) {
      form.setValue("directionOverride", defaultDirection)
    }
  }, [
    convertDistance,
    defaultDirection,
    defaultDistanceMmCurrent,
    defaultDistanceMmMax,
    form,
  ])

  const directionOverride = form.watch("directionOverride")
  const swathGeometry = runData
    ? getSwathGeometry({
        ...runData,
        distanceMmCurrent: defaultDistanceMmCurrent,
        distanceMmMax: defaultDistanceMmMax,
        extendHeadingDegrees: directionOverride,
        farmLocation,
      })
    : null

  return {
    actionName,
    centerLatLng: Geo.createMultiPoint(
      Geo.polygon(swathGeometry?.outlinePath?.native)?.getCenter(),
    ),
    deviceId,
    deviceSummary,
    form,
    onCloseForms: () => {
      form.reset()
      setActionName(undefined)
    },
    onPressSetDirection: withPermissions({
      callback: () => {
        setActionName("setRunDirection")
        trackEvent({
          // activeFarmId,
          elementName: "setReelRunDirectionLink",
          name: "element_press",
        })
      },
      required: "canManageDeviceConfiguration",
    }),
    onPressSetApplicationType: withPermissions({
      callback: () => {
        setActionName("setApplicationType")
        trackEvent({
          // activeFarmId,
          elementName: "setReelRunApplicationTypeLink",
          name: "element_press",
        })
      },
      required: "canManageDeviceConfiguration",
    }),
    onPressSetField: withPermissions({
      callback: () => {
        setActionName("setField")
        trackEvent({
          // activeFarmId,
          elementName: "setReelRunFieldLink",
          name: "element_press",
        })
      },
      required: "canManageDeviceConfiguration",
    }),
    onPressSetReelDistance: withPermissions({
      callback: () => {
        setActionName("setReelDistance")
        trackEvent({
          // activeFarmId,
          elementName: "setReelDistanceLink",
          name: "element_press",
        })
      },
      required: "canManageDeviceConfiguration",
    }),
    reelEvent,
    reelRunId: selectedRunId,
    runData,
    setActionName,
    swathGeometry,
    ...rest,
  }
}

type ContextValue = ReturnType<typeof useSelectedReelRun>

const Context = React.createContext<ContextValue | undefined>(undefined)

/**
 * Reel Run context provider
 *
 * NOTE: This component must be high up in the tree so that it can pass
 * props to child components that are both INSIDE of and OUTSIDE of map views.
 * On mobile in particular, this is an absolute requirement due to the way
 * React Native Maps renders its children; arbitrary cards, boxes, texts etc.
 * WILL NOT WORK if they are children of a map view.
 *
 * As a result we cannot render this component conditionally. Annoying right?
 *
 * Therefore, since the run polygons need to read from a useForm
 * hook, we have to always render the form.
 */
export function SelectedReelRunContextProvider({
  children,
  ...rest
}: AcceptsChildren & ProviderProps): React.JSX.Element | null {
  const value = useSelectedReelRun(rest)
  return <Context.Provider value={value}>{children}</Context.Provider>
}

export function useSelectedReelRunContext(): ContextValue {
  const ctx = React.useContext(Context)
  if (typeof ctx === "undefined") {
    throw new TypeError(`SelectedRun Context must be used inside of provider`)
  }
  return ctx
}
